import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { setDefaultSettings, selectFuseCurrentSettings } from 'app/store/fuse/settingsSlice';
import themesConfig from 'app/configs/themesConfig';
import {
  ThemeMode,
  getSavedThemeMode,
  saveThemeMode,
  getSystemThemeMode,
} from '@/utils/themeUtils';

export const useThemeMode = () => {
  const dispatch: ThunkDispatch<unknown, unknown, UnknownAction> = useDispatch();
  const settings = useSelector(selectFuseCurrentSettings);

  // Track the current preference (could be 'system')
  const [preference, setPreference] = useState<ThemeMode>(() => getSavedThemeMode() || 'system');

  // Get actual mode from current theme
  const currentMode = settings.theme?.main?.palette?.mode || 'light';

  // Toggle between light and dark (direct toggle, not 'system')
  const toggleThemeMode = useCallback(() => {
    const newMode = currentMode === 'light' ? 'dark' : 'light';

    // Save preference
    saveThemeMode(newMode);
    setPreference(newMode);

    // Update Redux state with appropriate theme
    const newTheme = newMode === 'dark' ? themesConfig.defaultDark : themesConfig.default;

    dispatch(
      setDefaultSettings({
        theme: {
          main: newTheme,
          navbar: themesConfig.defaultDark, // Keep navbar dark
          toolbar: newTheme,
          footer: themesConfig.defaultDark, // Keep footer dark
        },
      }),
    );
  }, [currentMode, dispatch]);

  // Set specific mode
  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      saveThemeMode(mode);
      setPreference(mode);

      const effectiveMode = mode === 'system' ? getSystemThemeMode() : mode;
      const newTheme = effectiveMode === 'dark' ? themesConfig.defaultDark : themesConfig.default;

      dispatch(
        setDefaultSettings({
          theme: {
            main: newTheme,
            navbar: themesConfig.defaultDark,
            toolbar: newTheme,
            footer: themesConfig.defaultDark,
          },
        }),
      );
    },
    [dispatch],
  );

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (preference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? themesConfig.defaultDark : themesConfig.default;
      dispatch(
        setDefaultSettings({
          theme: {
            main: newTheme,
            navbar: themesConfig.defaultDark,
            toolbar: newTheme,
            footer: themesConfig.defaultDark,
          },
        }),
      );
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference, dispatch]);

  return {
    mode: currentMode as 'light' | 'dark',
    preference,
    toggleThemeMode,
    setThemeMode,
    isDark: currentMode === 'dark',
  };
};
